import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listeningTimeService, type TrackListeningTimePayload, type ListeningTimeReportParams, type DailyListeningTimeData, type ListeningTimeStats } from '../services/listeningTime.service';
import { hasBufferedEntries } from '../services/listeningTimeBuffer';

// Query key factory
export const listeningTimeKeys = {
  all: ['listening-time'] as const,
  stats: () => [...listeningTimeKeys.all, 'stats'] as const,
  reports: () => [...listeningTimeKeys.all, 'reports'] as const,
  report: (params: ListeningTimeReportParams) => [...listeningTimeKeys.reports(), params] as const,
};

/** Returns today's date as YYYY-MM-DD in the user's local timezone. */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get listening time statistics
export function useListeningTimeStats() {
  return useQuery({
    queryKey: listeningTimeKeys.stats(),
    queryFn: () => listeningTimeService.getStats(),
    staleTime: 60000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// Get listening time report
export function useListeningTimeReport(params: ListeningTimeReportParams) {
  return useQuery({
    queryKey: listeningTimeKeys.report(params),
    queryFn: () => listeningTimeService.getReport(params),
    staleTime: 60000, // Consider data fresh for 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

// Get today's total listening time across all languages (used by ListeningTimeWidget)
export function useTodayListeningTime(): { totalSeconds: number } {
  const today = getTodayString();
  const params: ListeningTimeReportParams = { startDate: today, endDate: today };

  const { data } = useQuery({
    queryKey: listeningTimeKeys.report(params),
    queryFn: () => listeningTimeService.getReport(params),
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  const totalSeconds = data?.reduce((sum, d) => sum + d.totalSeconds, 0) ?? 0;
  return { totalSeconds };
}

export type MilestoneCheckCallback = (
  prevTotalSeconds: number,
  newTotalSeconds: number,
  dailyReport: DailyListeningTimeData[],
) => void;

interface TrackListeningTimeOptions {
  onMilestoneCheck?: MilestoneCheckCallback;
}

// Track listening time
export function useTrackListeningTime(options?: TrackListeningTimeOptions) {
  const queryClient = useQueryClient();
  // Stable ref so the mutation closure always calls the latest callback
  // without recreating the mutation when options reference changes
  const onMilestoneCheckRef = useRef(options?.onMilestoneCheck);
  useEffect(() => {
    onMilestoneCheckRef.current = options?.onMilestoneCheck;
  });

  return useMutation({
    mutationFn: (payload: TrackListeningTimePayload) =>
      listeningTimeService.track(payload),
    onMutate: async () => {
      // Snapshot all-time total from stats cache before the mutation.
      // Milestones are all-time thresholds (10h, 20h, 50h…), not daily ones.
      // Falls back to 0 if cache is cold — useCelebrations guards against
      // re-firing already-celebrated milestones via celebrated_milestones setting.
      const cachedStats = queryClient.getQueryData<ListeningTimeStats>(
        listeningTimeKeys.stats(),
      );
      const prevTotalSeconds = cachedStats?.languages.reduce((s, l) => s + l.totalSeconds, 0) ?? 0;
      return { prevTotalSeconds };
    },
    onSuccess: async (_data, _variables, context) => {
      await queryClient.invalidateQueries({ queryKey: listeningTimeKeys.stats() });
      await queryClient.invalidateQueries({ queryKey: listeningTimeKeys.reports() });

      if (onMilestoneCheckRef.current) {
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 6);
        // Use local timezone to match getTodayString() — avoids midnight boundary mismatch on UTC- zones
        const sevenDaysAgoStr = [
          sevenDaysAgo.getFullYear(),
          String(sevenDaysAgo.getMonth() + 1).padStart(2, '0'),
          String(sevenDaysAgo.getDate()).padStart(2, '0'),
        ].join('-');
        const today = getTodayString();

        const [newStats, weekReport] = await Promise.all([
          listeningTimeService.getStats(),
          listeningTimeService.getReport({ startDate: sevenDaysAgoStr, endDate: today }),
        ]);
        onMilestoneCheckRef.current(
          context?.prevTotalSeconds ?? 0,
          newStats.languages.reduce((s, l) => s + l.totalSeconds, 0),
          weekReport,
        );
      }
    },
    onError: (error: Error) => {
      console.error('Failed to track listening time (buffered for retry):', error);
    },
  });
}

/**
 * useOnlineListeningDrain — mount once at the app level (or on any player page).
 * Listens for the window 'online' event and drains any localStorage-buffered
 * listening time entries that failed to send during an offline period.
 * Also drains on mount in case connectivity was restored before this hook ran.
 */
export function useOnlineListeningDrain() {
  const queryClient = useQueryClient();

  useEffect(() => {
    async function drain() {
      if (!hasBufferedEntries()) return;
      console.log('[ListeningTime] Online — draining buffered entries');
      await listeningTimeService.drainBuffer();
      queryClient.invalidateQueries({ queryKey: listeningTimeKeys.stats() });
      queryClient.invalidateQueries({ queryKey: listeningTimeKeys.reports() });
    }

    // Drain immediately on mount (handles reconnect-before-mount case)
    drain();

    window.addEventListener('online', drain);
    return () => window.removeEventListener('online', drain);
  }, [queryClient]);
}
